import { useZone } from "@/lib/facility/zone";
import { formatLongDate, levelAllows } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "@/lib/icons";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { PolicyAttachments } from "@/components/app/policies/policy-attachments";
import { PolicyContent } from "@/components/app/policies/policy-content";
import { PolicyShareButton } from "@/components/app/policies/policy-share-button";
import { RelatedPolicies } from "@/components/app/policies/related-policies";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { PolicySignOffCard } from "@/routes/_authenticated/_knowledge/policies/-components/policy-sign-off-card";

export const Route = createFileRoute("/_authenticated/_knowledge/policies/$policyId/view")({
  component: PolicyView,
});

/** The published policy as it stands, to be read and signed off. Not the editing screen: the
 * signer signs a specific wording. Prints cleanly for paper filing. */
function PolicyView() {
  const zone = useZone();
  const { policyId } = Route.useParams();
  const canManage = useCan("policy:manage");

  const policy = useQuery({
    ...orpc.knowledge.policies.get.queryOptions({ input: { policyId } }),
    retry: false,
  });

  const published = policy.data?.versions.find(
    (version) => version.id === policy.data?.publishedVersionId,
  );

  if (policy.isPending) return <Skeleton className="h-96 w-full" />;
  if (policy.isError || !policy.data) {
    return (
      <EmptyState
        title="This policy could not be opened"
        description="It may have been removed, or it belongs to a building you cannot see."
        action={
          <Button asChild variant="outline">
            <Link to="/policies">All policies</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between print:hidden">
        <Button asChild variant="outline">
          <Link params={{ policyId }} to="/policies/$policyId">
            <ArrowLeft className="size-4" />
            Back to the policy
          </Link>
        </Button>
        <div className="flex flex-wrap gap-2">
          <PolicyShareButton
            policyId={policyId}
            title={policy.data.title}
            canChange={canManage || levelAllows(policy.data.myLevel, "manage")}
          />
          <Button onClick={() => window.print()} variant="outline">
            <Printer className="size-4" />
            Print policy
          </Button>
        </div>
      </div>

      <PageHeader
        title={policy.data.title}
        meta={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {policy.data.category ? <span>{policy.data.category}</span> : null}
            {published ? <span>Version {published.versionNumber}</span> : null}
            {published?.effectiveDate ? (
              <span>In force from {formatLongDate(published.effectiveDate, zone)}</span>
            ) : null}
          </span>
        }
      />

      {!published ? (
        <EmptyState
          title="Nothing is published yet"
          description="This policy has drafts but no version in force, so there is nothing to sign for."
        />
      ) : (
        <>
          <Card>
            <CardContent className="px-6 py-2">
              {/* A readable measure: at 1440px a full-width line ran past 150 characters. */}
              <PolicyContent content={published.content} className="max-w-3xl leading-relaxed" />
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <PolicyAttachments policyId={policyId} canEdit={false} />
            <RelatedPolicies policyId={policyId} canEdit={false} />
          </div>

          <PolicySignOffCard
            versionId={published.id}
            versionNumber={published.versionNumber}
            signOffRequired={policy.data.signOffRequired}
          />
        </>
      )}
    </div>
  );
}
